import { Alert, Stack } from "@mui/material";
import { FormTextField } from "components/Forms/FormTextField";
import { SkillAudience, SkillScope } from "generated-graphql/graphql";
import { Control, useFormState, useWatch } from "react-hook-form";

import type { SkillFileTreeController } from "./useSkillFileEditor";

import { SkillFileTreeEditor } from "./SkillFileTreeEditor";

export type SkillEditorFormValues = {
  displayName: string;
  description: string;
  scope: SkillScope;
  audience: SkillAudience;
  /** Disabled skills stay editable but Scout never offers them. */
  enabled: boolean;
};

type SkillEditorProps = {
  control: Control<SkillEditorFormValues>;
  readOnly?: boolean;
  /** The skill's files: on the server once saved, held locally while creating. */
  fileEditor: SkillFileTreeController;
  /** Remounts the tree view per skill, so half-named entries don't carry over. */
  fileTreeKey: string;
};

/** Shown while the skill is off, so it's obvious why Scout isn't using it. */
function SkillDisabledNotice({ control }: { control: Control<SkillEditorFormValues> }) {
  const enabled = useWatch({ control, name: "enabled" });
  const { dirtyFields } = useFormState({ control, name: "enabled" });
  if (enabled) {
    return null;
  }
  return (
    <Alert severity="info">
      {dirtyFields.enabled
        ? "Save to turn this skill off. Scout won't find or use it while it's off."
        : "This skill is off. Scout won't find or use it until you turn it back on."}
    </Alert>
  );
}

export function SkillEditor({
  control,
  readOnly = false,
  fileEditor,
  fileTreeKey,
}: SkillEditorProps) {
  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <SkillDisabledNotice control={control} />
      <FormTextField
        name="description"
        label="Description"
        control={control}
        fullWidth
        disabled={readOnly}
        textFieldProps={{ multiline: true, minRows: 2 }}
        rules={{ required: "Description is required" }}
      />

      <SkillFileTreeEditor key={fileTreeKey} editor={fileEditor} readOnly={readOnly} />
    </Stack>
  );
}
